// src/store/api/chatApi.js
import { baseApi } from "./baseApi";
import signalRService from "../../services/signalrService";
import { useDispatch } from "react-redux";
import { useEffect, useRef, useCallback, useState } from "react";
import {
  setOnline,
  setOffline,
  setInitialOnlineUsers,
} from "../onlineStatusSlice";
import { useSelector } from "react-redux";

// ===========================
// NOTIFICATION HELPERS
// Singleton audio instance avoids creating a new Audio object on every message.
// ===========================
let _notificationAudio = null;
const getNotificationAudio = () => {
  if (!_notificationAudio) {
    _notificationAudio = new Audio("/notification.mp3");
    _notificationAudio.volume = 0.3;
  }
  return _notificationAudio;
};

const triggerNotification = (message) => {
  // Browser notification (only when tab is hidden)
  if (
    document.hidden &&
    "Notification" in window &&
    Notification.permission === "granted"
  ) {
    new Notification("New message", {
      body: message || "You have a new message",
      icon: "/icon.png",
    });
  }

  // Audio notification — catch & ignore autoplay-policy errors
  try {
    getNotificationAudio()
      .play()
      .catch(() => {});
  } catch (_e) {
    // Silently ignore if Audio is not supported
  }
};

// ===========================
// NORMALIZATION HELPERS
// Convert snake_case / PascalCase API responses to a consistent camelCase shape.
// ===========================
const normalizeMessage = (msg) => ({
  id: msg.id ?? msg.Id,
  senderId: msg.senderId ?? msg.SenderId,
  receiverId: msg.receiverId ?? msg.ReceiverId,
  message: msg.message ?? msg.Message ?? null,
  mediaUrl: msg.mediaUrl ?? msg.MediaUrl ?? null,
  sentAt: msg.sentAt ?? msg.SentAt,
  isRead: msg.isRead ?? msg.IsRead ?? false,
  status: msg.status ?? "sent",
});

const normalizeChat = (chat) => ({
  otherUserId: chat.otherUserId ?? chat.OtherUserId,
  userName: chat.userName ?? chat.UserName,
  profilePhotoUrl: chat.profilePhotoUrl ?? chat.ProfilePhotoUrl ?? null,
  lastMessageText: chat.lastMessageText ?? chat.LastMessageText ?? null,
  lastSentAt: chat.lastSentAt ?? chat.LastSentAt,
  unreadCount: chat.unreadCount ?? chat.UnreadCount ?? 0,
  isRead: chat.isRead ?? chat.IsRead ?? true,
});

const normalizeChatRequest = (req) => ({
  id: req.id ?? req.Id,
  senderId: req.senderId ?? req.SenderId,
  receiverId: req.receiverId ?? req.ReceiverId,
  senderUserName: req.senderName ?? req.SenderUserName ?? "Unknown User",
  senderProfilePhoto: req.senderPhotoUrl ?? req.SenderProfilePhoto ?? null,
  message: req.firstMessage ?? req.FirstMessage ?? "Wants to connect with you",
  mediaUrl: req.mediaUrl ?? req.MediaUrl ?? null,
  sentAt: req.createdAt ?? req.CreatedAt,
  status: req.status ?? req.Status ?? 0,
});

/**
 * Normalize an accepted chat-request entry.
 * The "other" user may be sender or receiver depending on who initiated.
 */
const normalizeAcceptedRequest = (req) => ({
  id: req.id ?? req.Id,
  otherUserId:
    req.otherUserId ??
    req.OtherUserId ??
    req.receiverId ??
    req.ReceiverId ??
    req.senderId ??
    req.SenderId,
  userName:
    req.userName ??
    req.UserName ??
    req.receiverName ??
    req.ReceiverName ??
    req.senderUserName ??
    req.SenderUserName ??
    "Unknown User",
  profilePhotoUrl:
    req.profilePhotoUrl ??
    req.ProfilePhotoUrl ??
    req.receiverPhotoUrl ??
    req.ReceiverPhotoUrl ??
    req.senderProfilePhoto ??
    req.SenderProfilePhoto ??
    null,
  acceptedAt:
    req.acceptedAt ??
    req.AcceptedAt ??
    req.respondedAt ??
    req.RespondedAt ??
    req.updatedAt ??
    req.UpdatedAt ??
    null,
  firstMessage: req.firstMessage ?? req.FirstMessage ?? null,
});

// ===========================
// RTK QUERY ENDPOINTS
// ===========================
export const chatApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── GET PERSONAL MESSAGES (initial load — latest 20) ──────────────────
    getPersonalMessages: builder.query({
      query: (otherUserId) => `/Chat/personal/${otherUserId}?limit=20`,
      transformResponse: (response) => {
        const messages = response?.data?.$values ?? response?.data ?? [];
        return {
          messages: messages
            .map(normalizeMessage)
            .sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt)),
          nextCursor: response?.nextCursor ?? null,
          hasMore: response?.hasMore ?? false,
        };
      },
      providesTags: (_result, _error, otherUserId) => [
        { type: "Messages", id: otherUserId },
      ],
      keepUnusedDataFor: 60,
      refetchOnMountOrArgChange: true,
    }),

    // ── LOAD MORE MESSAGES (cursor-based pagination — older messages) ─────
    loadMoreMessages: builder.query({
      query: ({ otherUserId, cursor }) =>
        `/Chat/personal/${otherUserId}?cursor=${encodeURIComponent(cursor)}&limit=20`,
      transformResponse: (response) => {
        const messages = response?.data?.$values ?? response?.data ?? [];
        return {
          messages: messages
            .map(normalizeMessage)
            .sort((a, b) => new Date(a.sentAt) - new Date(b.sentAt)),
          nextCursor: response?.nextCursor ?? null,
          hasMore: response?.hasMore ?? false,
        };
      },
      // Results are merged manually into local state — no caching needed.
      keepUnusedDataFor: 0,
    }),

    // ── GET CHATS LIST ────────────────────────────────────────────────────
    getChats: builder.query({
      query: () => "/Chat/chats",
      transformResponse: (response) => {
        const chats = response?.data?.$values ?? response ?? [];
        return chats
          .map(normalizeChat)
          .sort((a, b) => new Date(b.lastSentAt) - new Date(a.lastSentAt));
      },
      providesTags: ["Chats"],
    }),

    // ── GET PENDING (RECEIVED) REQUESTS ───────────────────────────────────
    getPendingRequests: builder.query({
      query: () => "/Chat/chat-requests/pending",
      transformResponse: (response) => {
        const requests = response?.$values ?? response ?? [];
        return requests.map(normalizeChatRequest);
      },
      providesTags: ["Requests"],
    }),

    // ── GET SENT REQUESTS ─────────────────────────────────────────────────
    getSentRequests: builder.query({
      query: () => "/Chat/chat-requests/sent",
      transformResponse: (response) => {
        const requests = response?.$values ?? response ?? [];
        return requests.map((req) => ({
          id: req.id ?? req.Id,
          receiverId: req.receiverId ?? req.ReceiverId,
          receiverName: req.receiverName ?? req.ReceiverName ?? "Unknown User",
          receiverPhotoUrl:
            req.receiverPhotoUrl ?? req.ReceiverPhotoUrl ?? null,
          message: req.firstMessage ?? req.FirstMessage ?? "",
          mediaUrl: req.mediaUrl ?? req.MediaUrl ?? null,
          status: req.status ?? req.Status ?? 0,
          createdAt: req.createdAt ?? req.CreatedAt,
          respondedAt: req.respondedAt ?? req.RespondedAt ?? null,
        }));
      },
      providesTags: ["SentRequests"],
    }),

    // ── GET ACCEPTED REQUESTS ─────────────────────────────────────────────
    // Users whose chat request has been accepted (by either party).
    // Surfaces "ready to chat" users before any message has been exchanged.
    getAcceptedRequests: builder.query({
      query: () => "/Chat/chat-requests/accepted",
      transformResponse: (response) => {
        const requests =
          response?.data?.$values ??
          response?.data ??
          response?.$values ??
          (Array.isArray(response) ? response : []);
        return requests.map(normalizeAcceptedRequest);
      },
      providesTags: ["AcceptedRequests"],
    }),

    // ── ACCEPT CHAT REQUEST ───────────────────────────────────────────────
    acceptChatRequest: builder.mutation({
      query: (requestId) => ({
        url: `/Chat/chat-requests/${requestId}/accept`,
        method: "POST",
      }),
      invalidatesTags: ["Chats", "Requests", "AcceptedRequests"],
    }),

    // ── REJECT CHAT REQUEST ───────────────────────────────────────────────
    rejectChatRequest: builder.mutation({
      query: (requestId) => ({
        url: `/Chat/chat-requests/${requestId}/reject`,
        method: "POST",
      }),

      // Invalidate pending + accepted lists so UI updates instantly
      invalidatesTags: ["Requests", "AcceptedRequests", "Chats"],
    }),

    // ── SEND MESSAGE ──────────────────────────────────────────────────────
    sendMessage: builder.mutation({
      query: ({ receiverId, message, mediaFile, postId }) => {
        const formData = new FormData();
        formData.append("ReceiverId", receiverId);
        if (message) formData.append("Message", message);
        if (mediaFile) formData.append("MediaFile", mediaFile);
        if (postId) formData.append("PostId", postId);
        return { url: "/Chat/send-message", method: "POST", body: formData };
      },

      async onQueryStarted(
        { receiverId, message, mediaFile },
        { dispatch, queryFulfilled, getState },
      ) {
        const state = getState();
        const currentUserId =
          state.auth?.userId ?? localStorage.getItem("userId");
        if (!currentUserId) return;

        const conversationId = receiverId;
        const tempId = `temp-${Date.now()}`;
        const objectUrl = mediaFile ? URL.createObjectURL(mediaFile) : null;

        const optimisticMessage = {
          id: tempId,
          senderId: currentUserId,
          receiverId: conversationId,
          message,
          mediaUrl: objectUrl,
          sentAt: new Date().toISOString(),
          status: "sending",
          isRead: false,
        };

        // ── Check if cache entry exists for this conversation ──────────────────
        // RTK Query's updateQueryData is a no-op when no cache entry exists.
        // For a new conversation, getPersonalMessages has never been called so
        // there is no entry. We detect this and write the initial empty shape
        // directly into the store before patching.
        const existingCacheData =
          chatApi.endpoints.getPersonalMessages.select(conversationId)(
            getState(),
          )?.data;

        if (!existingCacheData) {
          // Seed an empty cache entry so the patch below has something to write into.
          // We use the internal RTK Query action to write fulfilled data directly,
          // bypassing any network request.
          dispatch(
            chatApi.util.upsertQueryData
              ? // upsertQueryData exists in RTK Query v2+
                // For v1, use the workaround below
                chatApi.util.upsertQueryData(
                  "getPersonalMessages",
                  conversationId,
                  { messages: [], nextCursor: null, hasMore: false },
                )
              : // RTK Query v1 fallback — patch with initializer
                chatApi.util.updateQueryData(
                  "getPersonalMessages",
                  conversationId,
                  () => ({ messages: [], nextCursor: null, hasMore: false }),
                ),
          );
        }

        // ── Optimistic insert ──────────────────────────────────────────────────
        const patchResult = dispatch(
          chatApi.util.updateQueryData(
            "getPersonalMessages",
            conversationId,
            (draft) => {
              if (!draft) return;
              if (!Array.isArray(draft.messages)) {
                draft.messages = [];
              }
              // Dedup guard — never insert the same temp twice
              if (!draft.messages.some((m) => m.id === tempId)) {
                draft.messages.push(optimisticMessage);
              }
            },
          ),
        );

        try {
          const { data } = await queryFulfilled;

          if (objectUrl) URL.revokeObjectURL(objectUrl);

          // ── Build confirmed message — handle BOTH API response shapes ──────
          //
          // Shape A (first message → chat request created):
          //   { "$id":"1", "isChatRequest": true, "id": 3056, "status": 0 }
          //   → no senderId, no message, no sentAt
          //   → keep ALL fields from optimistic, just upgrade the id
          //
          // Shape B (existing chat → full message object):
          //   { "id": 3131, "senderId": "...", "message": "hii", "sentAt": "..." }
          //   → normalize and merge, falling back to optimistic for missing fields

          const isChatRequest = data?.isChatRequest === true;

          let confirmedMessage;

          if (isChatRequest) {
            // Shape A — only the id is reliable from the server response.
            // Everything else comes from the optimistic message we already built.
            confirmedMessage = {
              ...optimisticMessage,
              id: data.id ?? tempId,
              status: "sent",
              mediaUrl: null, // object URL already revoked
            };
          } else {
            // Shape B — full message. Normalize then fill any gaps from optimistic.
            const normalized = normalizeMessage(data);
            confirmedMessage = {
              // Start with optimistic so no field is ever undefined
              ...optimisticMessage,
              // Overlay with server values (only overwrite if server gave a value)
              ...(normalized.id != null && { id: normalized.id }),
              ...(normalized.senderId != null && {
                senderId: normalized.senderId,
              }),
              ...(normalized.receiverId != null && {
                receiverId: normalized.receiverId,
              }),
              ...(normalized.message != null && {
                message: normalized.message,
              }),
              ...(normalized.sentAt != null && { sentAt: normalized.sentAt }),
              ...(normalized.mediaUrl != null && {
                mediaUrl: normalized.mediaUrl,
              }),
              ...(normalized.isRead != null && { isRead: normalized.isRead }),
              status: "sent",
              mediaUrl: objectUrl ? null : (normalized.mediaUrl ?? null),
            };
          }

          // ── Replace temp bubble with confirmed message ──────────────────────
          dispatch(
            chatApi.util.updateQueryData(
              "getPersonalMessages",
              conversationId,
              (draft) => {
                if (!draft?.messages) return;
                const idx = draft.messages.findIndex((m) => m.id === tempId);
                if (idx !== -1) {
                  draft.messages[idx] = confirmedMessage;
                } else {
                  // Temp was already cleared somehow — push confirmed if not duplicate
                  if (
                    !draft.messages.some((m) => m.id === confirmedMessage.id)
                  ) {
                    draft.messages.push(confirmedMessage);
                    draft.messages.sort(
                      (a, b) => new Date(a.sentAt) - new Date(b.sentAt),
                    );
                  }
                }
              },
            ),
          );

          // ── Update chats list preview ───────────────────────────────────────
          // NEVER call invalidateTags(['Chats']) here — it wipes currentData on
          // getPersonalMessages causing ChatWindow to blank the message list.
          //
          // IMPORTANT: the getChats cache must only be touched when this was a
          // real message in an (existing or accepted) chat. A first message that
          // the backend turns into a chat REQUEST must NOT appear in the Chats
          // tab — it belongs in "Sent" until the recipient accepts. Writing a
          // placeholder row here was producing a phantom "User" conversation.
          if (isChatRequest) {
            // First message created a chat REQUEST — refresh request lists only.
            dispatch(
              chatApi.util.invalidateTags([
                "SentRequests",
                "AcceptedRequests",
                "Requests",
              ]),
            );
          } else {
            // Regular message — update the chat list preview and bubble to top.
            dispatch(
              chatApi.util.updateQueryData(
                "getChats",
                undefined,
                (draft = []) => {
                  const chatIdx = draft.findIndex(
                    (c) => c.otherUserId === conversationId,
                  );

                  if (chatIdx !== -1) {
                    draft[chatIdx].lastMessageText = confirmedMessage.message;
                    draft[chatIdx].lastSentAt = confirmedMessage.sentAt;
                    draft[chatIdx].unreadCount = 0;
                    draft[chatIdx].isRead = true;
                    const [chat] = draft.splice(chatIdx, 1);
                    draft.unshift(chat);
                  } else {
                    // Accepted-but-not-yet-listed conversation: insert a row.
                    // userName is filled in on the next getChats fetch.
                    draft.unshift({
                      otherUserId: conversationId,
                      userName: "User",
                      profilePhotoUrl: null,
                      lastMessageText: confirmedMessage.message,
                      lastSentAt: confirmedMessage.sentAt,
                      unreadCount: 0,
                      isRead: true,
                    });
                  }
                },
              ),
            );
            dispatch(
              chatApi.util.invalidateTags(["SentRequests", "AcceptedRequests"]),
            );
          }
        } catch (err) {
          patchResult.undo();
          if (objectUrl) URL.revokeObjectURL(objectUrl);
          console.error("Failed to send message:", err);
          throw err;
        }
      },
    }),
    // ── MARK AS READ ──────────────────────────────────────────────────────
    markAsRead: builder.mutation({
      query: (otherUserId) => ({
        url: `/Chat/personal/${otherUserId}/mark-read`,
        method: "POST",
      }),

      async onQueryStarted(otherUserId, { dispatch, queryFulfilled }) {
        const patchMessages = dispatch(
          chatApi.util.updateQueryData(
            "getPersonalMessages",
            otherUserId,
            (draft) => {
              draft.messages?.forEach((msg) => {
                if (!msg.isRead) msg.isRead = true;
              });
            },
          ),
        );

        const patchChats = dispatch(
          chatApi.util.updateQueryData("getChats", undefined, (draft = []) => {
            const chat = draft.find((c) => c.otherUserId === otherUserId);
            if (chat) {
              chat.unreadCount = 0;
              chat.isRead = true;
            }
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchMessages.undo();
          patchChats.undo();
        }
      },
    }),

    // ── CLEAR CHAT ────────────────────────────────────────────────────────
    clearChat: builder.mutation({
      query: (otherUserId) => ({
        url: `/Chat/personal/${otherUserId}/clear`,
        method: "DELETE",
      }),
      invalidatesTags: ["Messages", "Chats"],
    }),

    // ── REMOVE USER ───────────────────────────────────────────────────────
    removeUser: builder.mutation({
      query: (otherUserId) => ({
        url: `/Chat/personal/${otherUserId}/remove`,
        method: "DELETE",
      }),
      invalidatesTags: ["Chats"],
    }),
  }),
});

// ===========================
// GLOBAL SIGNALR HOOK  (mount once at the app root — never unmounts)
//
// Responsible for:
//   • Establishing and maintaining the SignalR connection for the logged-in user
//   • Updating the getChats cache (unread counts, last message preview) on every
//     incoming message — regardless of which page the user is currently on
//   • Updating the getPersonalMessages cache so the conversation is already
//     populated when the user opens the chat page
//   • Firing browser / audio notifications
//
// activeChatId — the conversationId that is currently open on the chat page.
//   Pass null when the user is not on the chat page. When null, every incoming
//   message increments the unread count, which is exactly the correct behaviour.
//
// WHY this must live at the app root and NOT inside ChatPage:
//   ChatPage unmounts when the user navigates away. When it unmounts the
//   ReceiveMessage handler is torn down, SignalR stops updating the cache, and
//   unread counts never change until the user returns to the chat page.
// ===========================
export const useGlobalSignalRChat = (currentUserId, activeChatId = null) => {
  const dispatch = useDispatch();
  const currentUserIdRef = useRef(currentUserId);
  const activeChatRef = useRef(activeChatId);
  const [connectionState, setConnectionState] = useState("connecting");

  // Keep refs in sync without re-running the heavy effects
  useEffect(() => {
    currentUserIdRef.current = currentUserId;
  }, [currentUserId]);
  useEffect(() => {
    activeChatRef.current = activeChatId;
  }, [activeChatId]);

  /**
   * Handle one incoming message from SignalR.
   * Stable reference — registered once per connection lifetime.
   */
  const onReceiveMessage = useCallback(
    (rawMessage) => {
      const userId = currentUserIdRef.current;
      if (!userId) return;

      const msg = normalizeMessage({
        ...rawMessage,
        status: "received",
        isRead: false,
      });

      // ── Ownership guards ───────────────────────────────────────────────────
      // Guard 1: Ignore echoes of our own optimistically-sent messages.
      if (msg.senderId === userId) return;

      // Guard 2: If the backend included receiverId in the payload, verify it
      // matches the current user. This prevents a message intended for another
      // user (e.g. delivered to the wrong connection after a reconnect) from
      // contaminating the current user's conversation cache.
      // The check is conditional — if receiverId is absent (older backend), we
      // skip it rather than dropping all messages.
      if (msg.receiverId && msg.receiverId !== userId) return;

      // Guard 3: senderId must be present — a message with no sender is malformed.
      if (!msg.senderId) return;

      const otherUserId = msg.senderId;
      const isActiveConversation = activeChatRef.current === otherUserId;

      // ── 1. Update personal messages cache (if the cache entry exists) ─────
      // Only patches when the user has already opened this conversation at
      // least once. If they haven't, the cache entry doesn't exist yet and
      // updateQueryData is a no-op — the full fetch happens when they open it.
      dispatch(
        chatApi.util.updateQueryData(
          "getPersonalMessages",
          otherUserId,
          (draft) => {
            if (!draft?.messages) return;
            const isDuplicate = draft.messages.some((m) => m.id === msg.id);
            if (!isDuplicate) {
              draft.messages.push(msg);
              draft.messages.sort(
                (a, b) => new Date(a.sentAt) - new Date(b.sentAt),
              );
            }
          },
        ),
      );

      // ── 2. Update chats list — this is what drives the unread badge ────────
      dispatch(
        chatApi.util.updateQueryData("getChats", undefined, (draft = []) => {
          const chatIdx = draft.findIndex((c) => c.otherUserId === otherUserId);

          if (chatIdx !== -1) {
            const chat = draft[chatIdx];

            // Dedup guard — avoid processing the same message twice
            if (chat._lastMessageId === msg.id) return;
            chat._lastMessageId = msg.id;

            chat.lastMessageText = msg.message;
            chat.lastSentAt = msg.sentAt;

            if (isActiveConversation) {
              // User is looking at this conversation right now — mark read
              chat.unreadCount = 0;
              chat.isRead = true;
            } else {
              // User is elsewhere — increment unread badge
              chat.unreadCount = (chat.unreadCount ?? 0) + 1;
              chat.isRead = false;
            }

            // Bubble to top of the list
            const [updated] = draft.splice(chatIdx, 1);
            draft.unshift(updated);
          } else {
            // First message from this sender — create a new chat entry
            draft.unshift({
              otherUserId,
              userName: msg.senderName ?? "New User",
              profilePhotoUrl: msg.senderPhoto ?? null,
              lastMessageText: msg.message,
              lastSentAt: msg.sentAt,
              unreadCount: isActiveConversation ? 0 : 1,
              isRead: isActiveConversation,
              _lastMessageId: msg.id,
            });
          }
        }),
      );

      // ── 3. Notifications ───────────────────────────────────────────────────
      // Only notify when the conversation is not currently open and visible
      if (!isActiveConversation) {
        triggerNotification(msg.message);
      }
    },
    [dispatch],
  );

  // ── ChatListUpdated handler ──────────────────────────────────────────────
  // Fired by the backend when:
  //   a) Someone marks our messages as read → UnreadCount drops to 0
  //   b) A new message arrives and the backend wants to sync the chat list
  //      (supplements ReceiveMessage — handles cases where ReceiveMessage
  //       was missed or the chat entry doesn't exist in our cache yet)
  //
  // Payload from backend:
  //   { OtherUserId, UnreadCount, LastMessageText?, LastSentAt? }
  const onChatListUpdated = useCallback(
    (payload) => {
      const otherUserId = payload?.OtherUserId ?? payload?.otherUserId;
      if (!otherUserId) return;

      const unreadCount = payload?.UnreadCount ?? payload?.unreadCount ?? 0;
      const lastMessage =
        payload?.LastMessageText ?? payload?.lastMessageText ?? null;
      const lastSentAt = payload?.LastSentAt ?? payload?.lastSentAt ?? null;

      dispatch(
        chatApi.util.updateQueryData("getChats", undefined, (draft = []) => {
          const chatIdx = draft.findIndex((c) => c.otherUserId === otherUserId);

          if (chatIdx !== -1) {
            const chat = draft[chatIdx];
            chat.unreadCount = unreadCount;
            chat.isRead = unreadCount === 0;
            if (lastMessage !== null) chat.lastMessageText = lastMessage;
            if (lastSentAt !== null) chat.lastSentAt = lastSentAt;

            // Re-bubble to top if a new message caused this update
            if (lastSentAt !== null) {
              const [updated] = draft.splice(chatIdx, 1);
              draft.unshift(updated);
            }
          }
          // If the chat entry doesn't exist yet, a full refetch will happen
          // the next time the user opens the chat list — no action needed here.
        }),
      );
    },
    [dispatch],
  );

  // ── Presence event handlers ──────────────────────────────────────────────
  // Stable callbacks — registered once, removed only on unmount.
  const onUserOnline = useCallback(
    (userId) => {
      if (userId) dispatch(setOnline(userId));
    },
    [dispatch],
  );

  const onUserOffline = useCallback(
    (userId) => {
      if (userId) dispatch(setOffline(userId));
    },
    [dispatch],
  );

  const onInitialOnlineUsers = useCallback(
    (userIds) => {
      dispatch(setInitialOnlineUsers(userIds ?? []));
    },
    [dispatch],
  );

  // ── SignalR connection lifecycle ──────────────────────────────────────────
  useEffect(() => {
    if (!currentUserId) return;

    let isMounted = true;

    const startConnection = async () => {
      try {
        const token = localStorage.getItem("authToken");

        if (!signalRService.isConnected()) {
          await signalRService.connect(token);
        }

        if (!isMounted) return;

        setConnectionState("connected");

        // Pass the specific callback to off() so we only remove OUR handler.
        // Never call off(event) with no callback — that removes ALL handlers
        // for the event, including those registered by other hook instances
        // (e.g. if two components both call this hook).
        signalRService.off("ReceiveMessage", onReceiveMessage);
        signalRService.on("ReceiveMessage", onReceiveMessage);

        // ── Chat list sync (read-receipts, real-time list updates) ──────────
        signalRService.off("ChatListUpdated", onChatListUpdated);
        signalRService.on("ChatListUpdated", onChatListUpdated);

        // ── Presence events ────────────────────────────────────────────────
        // Register with the specific callback so off() can target only ours.
        //
        // Expected hub event names (ask your backend dev to confirm):
        //   UserOnline(userId: string)
        //   UserOffline(userId: string)
        //   OnlineUsers(userIds: string[])  — sent once on connect with
        //                                     the full list of online users
        //
        // If your backend uses different names, change the strings below.
        signalRService.off("UserOnline", onUserOnline);
        signalRService.on("UserOnline", onUserOnline);

        signalRService.off("UserOffline", onUserOffline);
        signalRService.on("UserOffline", onUserOffline);

        signalRService.off("OnlineUsers", onInitialOnlineUsers);
        signalRService.on("OnlineUsers", onInitialOnlineUsers);

        signalRService.connection?.onreconnecting(() => {
          if (isMounted) setConnectionState("reconnecting");
        });
        signalRService.connection?.onreconnected(() => {
          if (isMounted) setConnectionState("connected");
          // Re-register all handlers after reconnect
          signalRService.off("ReceiveMessage", onReceiveMessage);
          signalRService.on("ReceiveMessage", onReceiveMessage);
          signalRService.off("ChatListUpdated", onChatListUpdated);
          signalRService.on("ChatListUpdated", onChatListUpdated);
          signalRService.off("UserOnline", onUserOnline);
          signalRService.on("UserOnline", onUserOnline);
          signalRService.off("UserOffline", onUserOffline);
          signalRService.on("UserOffline", onUserOffline);
          signalRService.off("OnlineUsers", onInitialOnlineUsers);
          signalRService.on("OnlineUsers", onInitialOnlineUsers);
        });
        signalRService.connection?.onclose(() => {
          if (isMounted) setConnectionState("disconnected");
        });
      } catch (err) {
        console.error("SignalR connection failed:", err);
        if (isMounted) setConnectionState("disconnected");
      }
    };

    startConnection();

    return () => {
      isMounted = false;
      // Remove only our specific handlers — never bare off(event) which
      // would wipe every registration including other hook instances.
      signalRService.off("ReceiveMessage", onReceiveMessage);
      signalRService.off("ChatListUpdated", onChatListUpdated);
      signalRService.off("UserOnline", onUserOnline);
      signalRService.off("UserOffline", onUserOffline);
      signalRService.off("OnlineUsers", onInitialOnlineUsers);
    };
  }, [
    currentUserId,
    onReceiveMessage,
    onChatListUpdated,
    onUserOnline,
    onUserOffline,
    onInitialOnlineUsers,
  ]);

  return { connectionState };
};

// ===========================
// LEGACY ALIAS — keeps ChatPage working without changes.
// ChatPage can pass activeChatId so the global hook knows not to
// increment unread count for the conversation currently on screen.
// ===========================
export const useSignalRChat = useGlobalSignalRChat;

// ===========================
// EXPORTED HOOKS
// ===========================
export const {
  useGetPersonalMessagesQuery,
  useGetChatsQuery,
  useSendMessageMutation,
  useMarkAsReadMutation,
  useClearChatMutation,
  useRemoveUserMutation,
  useGetPendingRequestsQuery,
  useGetSentRequestsQuery,
  useGetAcceptedRequestsQuery,
  useRejectChatRequestMutation,
  useAcceptChatRequestMutation,
  useLazyLoadMoreMessagesQuery,
} = chatApi;
